import * as tokens from '@nicolaudie-group/ng-ui-kit/helpers/tokens'
import splitPath from '@nicolaudie-group/ng-ui-kit/helpers/splitPath'
import PbError from '@/classes/PbError.class'
import routeNames from '@/router/names'
import pinia from '@/stores/pinia'
import useRootStore from '@/stores/root.store'
import useUserStore from '@/stores/user.store'

/**
 * Hook called by the router (`SSORedirect` route) when Nicolaudie
 * Connect redirects the user to the webapp after login.
 *
 * It first checks the claims of the token provided by the backend in the `at` query.
 * If it's valid, it gets stored by the user store and the user is re-loaded.
 * If it's not valid, the user gets redirected to login page, after logging out
 * to clear the cookies.
 *
 * @param {Object} to - Route that triggered the hook (`SSORedirect`)
 * @returns {Object} RouteLocation where the router must redirect (`goto` query)
 */
export const loginRedirect = async (to) => {
  const userStore = useUserStore(pinia)
  const rootStore = useRootStore(pinia)
  // Check and store the token
  const valid = await userStore.checkToken(to.query.at)
  const goto = decodeURIComponent(to.query.goto) || '/'

  // If the token is valid
  if (valid) {
    // Carry on to the goto page or home
    return goto
  }

  // If the token is not valid

  // Trigger the redirection timeout
  // keepSession flag is false here, since the token was invalid we should clean it from DB
  userStore.goToLogout({ goto, time: 5000, keepSession: false })

  // Add the error
  rootStore.addError(new PbError({
    title: 'Invalid token',
    message: 'The token is invalid. You\'ll be redirected to the login page in 5 seconds.',
    page: true,
  }))

  // Redirect to the Error page, in order to have set the URL to the goto value
  // That way, the user can refresh the page and have the correct page in case of
  // success, instead of a generic page
  return {
    name: routeNames.Error,
    params: {
      pathMatch: splitPath(goto),
    },
  }
}

/**
 * Hook called on route change by the router, and periodically after
 * initialization of the `ready` hook
 *
 * - It first retrieves the token in the `dev_at` query or the local storage
 * - It then checks the claims of the token
 * - If it's valid, it gets stored by the user store and the user is re-loaded if the token changed
 * - If it's not valid, the user gets redirected to the login page, after logging out, unless
 * the destination (or current in case of the periodic check) route has the
 * `bypassAuth` flag in it's meta
 *
 * @param {Object} to - Called route
 * @returns {Boolean} True if the token was valid and
 * the router should continue to called route, false otherwise
 */
export const checkAuth = async (to) => {
  const userStore = useUserStore(pinia)

  // Get the token from query for dev or from localStorage
  const token = to.query.dev_at || tokens.get()

  // Check and store the token
  const valid = await userStore.checkToken(token)

  // If the route does not need login or the token is valid
  if (to.meta.bypassAuth || valid) {
    // Carry on to the route
    return true
  }

  // If the token is not valid, redirect to logout
  // keepSession flag is true here, since the user didn't ask to logout
  return userStore.goToLogout({ goto: to, keepSession: true })
}
